/**
 * cues-ui.ts — procedural UI SFX builders (v2M8.2).
 *
 * Implements AUDIO §10's four UI cues as native Web Audio oscillator
 * graphs. Each builder returns the *primary* `AudioScheduledSourceNode`
 * (started by `AudioEngine.playCue`); secondary sources (e.g. the
 * confirm tone in panel-open) are wired and `start()`-ed inside the
 * builder so the engine only manages the primary's lifecycle.
 *
 * Tuning targets per spec:
 *   - **Click**: 40 ms mid-range transient (~1.5 kHz triangle, fast decay).
 *   - **Hover**: 20 ms soft tick at -12 dB relative to click (~2.5 kHz sine).
 *   - **Panel open**: 200 ms band-pass-noise sweep up + 800 Hz confirm sine
 *     bloop ~120 ms in.
 *   - **Panel close**: 150 ms reverse — band-pass-noise sweep down.
 *
 * All four route through the UI bus (non-positional, not subject to
 * camera attenuation per §9). Volumes peak around 0.2–0.4 of the bus
 * gain so the bus itself can ride at 1.0 without clipping when several
 * cues overlap.
 */

import type { AudioEngine } from './engine';

/** Register all four §10 UI cues against the engine. */
export function registerUiCues(engine: AudioEngine): void {
  engine.registry.registerProcedural('ui:click', 'ui', buildUiClick);
  engine.registry.registerProcedural('ui:hover', 'ui', buildUiHover);
  engine.registry.registerProcedural('ui:panel-open', 'ui', buildUiPanelOpen);
  engine.registry.registerProcedural('ui:panel-close', 'ui', buildUiPanelClose);
}

function buildUiClick(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 1500;
  const gain = ctx.createGain();
  // Fast attack, ~40 ms exponential decay. Start near zero so the
  // exponential ramp can start (exponentialRampTo can't begin from 0).
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.4, now + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
  osc.connect(gain);
  gain.connect(target);
  // Recent Chrome rejects stop() scheduled before start(). Build functions
  // call start(now) on the returned node themselves; engine.ts's redundant
  // start() at playCue tail is caught defensively (line ~310). Same pattern
  // applies to every build function below.
  osc.start(now);
  osc.stop(now + 0.05);
  return osc;
}

function buildUiHover(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 2500;
  const gain = ctx.createGain();
  // -12 dB ≈ 0.25× amplitude relative to click's 0.4 peak → ~0.1.
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.1, now + 0.001);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
  osc.connect(gain);
  gain.connect(target);
  osc.start(now);
  osc.stop(now + 0.025);
  return osc;
}

function buildUiPanelOpen(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Primary: white-noise burst, band-pass-filtered, frequency sweeping
  // 300 Hz → 2.5 kHz over 160 ms.
  const noise = createNoiseSource(ctx, 0.18);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 4;
  filter.frequency.setValueAtTime(300, now);
  filter.frequency.exponentialRampToValueAtTime(2500, now + 0.16);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.001, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.2, now + 0.04);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(target);

  // Secondary: 800 Hz sine bloop confirming the panel landed. Starts
  // ~120 ms in, fades out by 200 ms total. Wired and started here;
  // engine only manages the primary noise source.
  const tone = ctx.createOscillator();
  tone.type = 'sine';
  tone.frequency.value = 800;
  const toneGain = ctx.createGain();
  toneGain.gain.setValueAtTime(0.0001, now + 0.12);
  toneGain.gain.exponentialRampToValueAtTime(0.15, now + 0.13);
  toneGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  tone.connect(toneGain);
  toneGain.connect(target);
  tone.start(now + 0.12);
  tone.stop(now + 0.21);
  return noise;
}

function buildUiPanelClose(ctx: AudioContext, target: AudioNode): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Reverse of panel-open: filter sweeps high → low, no confirm tone.
  const noise = createNoiseSource(ctx, 0.15);
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 4;
  filter.frequency.setValueAtTime(2500, now);
  filter.frequency.exponentialRampToValueAtTime(300, now + 0.14);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(target);
  return noise;
}

/**
 * Create a one-shot white-noise `AudioBufferSource` of the given duration
 * (in seconds). Buffer is constructed fresh per call — it's small (a few
 * thousand samples) and gets GC'd with the source on `onended`. Avoids
 * caching state between cue plays.
 */
function createNoiseSource(ctx: AudioContext, durationSec: number): AudioBufferSourceNode {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  return source;
}
